import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COLLECTIONS } from '@painttheworld/shared';
import { db } from '../backend/firebase';
import firebaseBackend, { auth } from '../backend/firebase';
import { useAppSelector } from '../redux/hooks';
import { selectUid } from '../redux/features/auth/slice';
import { track } from '../helpers/analytics';
import { timeAgo } from '../helpers/timeAgo';
import { signInWithGoogle } from '../helpers/googleSignIn';
import { signInWithApple } from '../helpers/appleSignIn';
import { t } from '../i18n';
import './Forum.scss';

const BODY_MAX = 2000;

interface ThreadDoc {
    title: string;
    body: string;
    authorName: string;
    colorHex: string;
    replyCount: number;
    createdAt: { toMillis?: () => number } | null;
}

interface ReplyDoc {
    id: string;
    body: string;
    authorName: string;
    colorHex: string;
    createdAt: { toMillis?: () => number } | null;
}

const ForumThread = () => {
    const { threadId } = useParams<{ threadId: string }>();
    const uid = useAppSelector(selectUid);
    const isSignedIn = uid !== null && !!auth.currentUser && !auth.currentUser.isAnonymous;

    const [thread, setThread] = useState<ThreadDoc | null>(null);
    const [replies, setReplies] = useState<ReplyDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (threadId) track('forum_thread_opened', { threadId });
    }, [threadId]);

    const loadThread = useCallback(async () => {
        if (!threadId) return;
        try {
            const threadSnap = await getDoc(doc(db, COLLECTIONS.FORUM_THREADS, threadId));
            if (!threadSnap.exists()) {
                setNotFound(true);
                return;
            }
            const data = threadSnap.data() as any;
            setThread({
                title: data.title ?? t('web.forum.untitled'),
                body: data.body ?? '',
                authorName: data.authorName ?? t('web.forum.defaultAuthor'),
                colorHex: data.colorHex ?? '#4CAF50',
                replyCount: typeof data.replyCount === 'number' ? data.replyCount : 0,
                createdAt: data.createdAt ?? null,
            });

            const repliesSnap = await getDocs(
                query(
                    collection(db, COLLECTIONS.FORUM_THREADS, threadId, COLLECTIONS.FORUM_REPLIES),
                    orderBy('createdAt', 'asc')
                )
            );
            setReplies(
                repliesSnap.docs.map((d) => {
                    const r = d.data() as any;
                    return {
                        id: d.id,
                        body: r.body ?? '',
                        authorName: r.authorName ?? t('web.forum.defaultAuthor'),
                        colorHex: r.colorHex ?? '#4CAF50',
                        createdAt: r.createdAt ?? null,
                    };
                })
            );
        } catch (err) {
            console.error('[ForumThread] failed to load thread:', err);
            setError(t('web.forum.loadThreadError'));
        } finally {
            setLoading(false);
        }
    }, [threadId]);

    useEffect(() => {
        loadThread();
    }, [loadThread]);

    const trimmed = replyText.trim();
    const canSubmit = trimmed.length >= 1 && trimmed.length <= BODY_MAX && !submitting;

    const handleSubmit = async () => {
        if (!canSubmit || !threadId) return;
        setSubmitting(true);
        setError(null);
        try {
            const createReply = httpsCallable<{ threadId: string; body: string }, { replyId: string }>(
                getFunctions(firebaseBackend),
                'createForumReply'
            );
            await createReply({ threadId, body: trimmed });
            track('forum_reply_created', { threadId });
            setReplyText('');
            setLoading(true);
            await loadThread();
        } catch (err: any) {
            console.error('[ForumThread] createForumReply failed:', err);
            setError(err?.message || t('web.forum.replyError'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="forum-page">
            <div className="forum-topbar">
                <Link to="/forum" className="forum-back">{t('web.forum.backToForum')}</Link>
            </div>

            <div className="forum-thread-content">
            {loading && !thread ? (
                <p className="forum-loading">{t('web.common.loading')}</p>
            ) : notFound ? (
                <p className="forum-empty">{t('web.forum.threadGone')}</p>
            ) : thread ? (
                <>
                    <div className="forum-thread-body" style={{ ['--accent' as any]: thread.colorHex }}>
                        <h1 className="forum-thread-heading">{thread.title}</h1>
                        <div className="thread-card-meta">
                            <span className="thread-card-author">{thread.authorName}</span>
                            {thread.createdAt && (
                                <>
                                    <span className="thread-card-dot">·</span>
                                    <span>{timeAgo(thread.createdAt)}</span>
                                </>
                            )}
                        </div>
                        <p className="forum-thread-text">{thread.body}</p>
                    </div>

                    <p className="forum-section-label">
                        {replies.length === 1
                            ? t('web.forum.repliesLabelOne', { count: replies.length })
                            : t('web.forum.repliesLabelOther', { count: replies.length })}
                    </p>

                    {error && <p className="forum-error">{error}</p>}

                    {replies.length > 0 && (
                        <ul className="forum-replies">
                            {replies.map((r) => (
                                <li key={r.id} className="forum-reply" style={{ ['--accent' as any]: r.colorHex }}>
                                    <p className="forum-reply-text">{r.body}</p>
                                    <div className="thread-card-meta">
                                        <span className="thread-card-author">{r.authorName}</span>
                                        {r.createdAt && (
                                            <>
                                                <span className="thread-card-dot">·</span>
                                                <span>{timeAgo(r.createdAt)}</span>
                                            </>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {isSignedIn ? (
                        <div className="forum-compose">
                            <p className="forum-compose-heading">{t('web.forum.addReply')}</p>
                            <textarea
                                className="forum-textarea"
                                placeholder={t('web.forum.replyPlaceholder')}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                            />
                            <div className="forum-compose-footer">
                                <span className={`forum-charcount${trimmed.length > BODY_MAX ? ' is-over' : ''}`}>
                                    {trimmed.length}/{BODY_MAX}
                                </span>
                                <button className="forum-btn" onClick={handleSubmit} disabled={!canSubmit}>
                                    {submitting ? t('web.forum.posting') : t('web.forum.postReply')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="forum-signin-cta">
                            <p>{t('web.forum.signInToJoin')}</p>
                            <button className="forum-btn" onClick={signInWithGoogle}>{t('web.auth.signInWithGoogle')}</button>
                            <button className="forum-btn forum-btn--apple" onClick={signInWithApple}>{t('web.auth.signInWithApple')}</button>
                        </div>
                    )}
                </>
            ) : (
                <p className="forum-empty">{error || t('web.common.somethingWentWrong')}</p>
            )}
            </div>
        </div>
    );
};

export default ForumThread;
